import DarkModeIcon from '@mui/icons-material/DarkMode';
import HelpIcon from "@mui/icons-material/Help";
import HomeIcon from "@mui/icons-material/Home";
import LightModeIcon from '@mui/icons-material/LightMode';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import Box from "@mui/material/Box";
import MuiIconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from '@mui/material/Menu';
import MenuItem from "@mui/material/MenuItem";
import { useColorScheme } from "@mui/material/styles";
import { useStytchB2BClient, useStytchMember } from '@stytch/react/b2b';
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAppContext } from '../../AppContext';
import { useCurrentUser } from '../../providers/currentUser/CurrentUserProvider';
import Avatar from "../common/Avatar";
import ScopeSelector from "./ScopeSelector";

export interface ToolbarProps {
    onHomeClick: (event: React.MouseEvent<HTMLElement>) => void;
    onHelpClick: (event: React.MouseEvent<HTMLElement>) => void;
}

const Toolbar = ({ onHomeClick, onHelpClick }: ToolbarProps) => {
    const [userPopoverAnchorEl, setUserPopoverAnchorEl] = useState<HTMLButtonElement | null>(null);
    const stytch = useStytchB2BClient();
    const { member } = useStytchMember();
    const { mode, setMode } = useColorScheme();
    const app = useAppContext();
    const navigate = useNavigate();
    const { user } = useCurrentUser();

    // Editing a user's name changes it here, not in Stytch, so the Stytch member keeps whatever name it was
    // invited with. Prefer our own record and fall back to Stytch only until it has loaded.
    const displayName = user ? `${user.firstName} ${user.lastName}` : (member?.name ?? "");

    const handleToggleTheme = () => {
        setMode(mode === 'light' ? 'dark' : 'light');
    };

    const handleLogOutClick = async () => {
        const slug = app.organizationSlug;
        await stytch.session.revoke();
        app.reset();
        navigate(`/${slug}`);
    };

    return (
        <Box ml="auto" display="flex" alignItems="center" gap={1}>
            <ScopeSelector />
            <MuiIconButton
                size="large"
                edge="end"
                aria-label="home"
                component={Link} to={`/${app.organizationSlug}`}
                onClick={onHomeClick}
                color="inherit"
            >
                <HomeIcon />
            </MuiIconButton>
            <MuiIconButton
                size="large"
                edge="end"
                aria-label="help"
                onClick={onHelpClick}
                color="inherit"
                sx={{ display: "none" }}
            >
                <HelpIcon />
            </MuiIconButton>
            <MuiIconButton
                id="user-avatar"
                size="large"
                edge="end"
                onClick={(e) => setUserPopoverAnchorEl(e.currentTarget)}
                aria-label="account of current user"
                aria-haspopup="true"
                color="inherit"
            >
                <Avatar name={displayName} />
            </MuiIconButton>

            <Menu
                anchorEl={userPopoverAnchorEl}
                open={Boolean(userPopoverAnchorEl)}
                onClose={() => setUserPopoverAnchorEl(null)}
                aria-labelledby="user-avatar"
            >
                <MenuItem component={Link} to={"/profile"}>
                    <ListItemIcon>
                        <PersonIcon fontSize="medium" />
                    </ListItemIcon>
                    <ListItemText>{displayName}</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleToggleTheme}>
                    <ListItemIcon>
                        {mode === 'light' ? (
                            <DarkModeIcon fontSize="medium" />
                        ) : (
                            <LightModeIcon fontSize="medium" />
                        )}
                    </ListItemIcon>
                    <ListItemText>
                        {mode === 'light' ? 'Dark Mode' : 'Light Mode'}
                    </ListItemText>
                </MenuItem>
                <MenuItem onClick={handleLogOutClick}>
                    <ListItemIcon>
                        <LogoutIcon fontSize="medium" />
                    </ListItemIcon>
                    <ListItemText>Sign Out</ListItemText>
                </MenuItem>
            </Menu>
        </Box>
    );
}

export default Toolbar;
